import React, { useState, useEffect, useCallback } from 'react';
import { inviteCodesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './InviteCodes.css';

const STATUS_COLORS = {
  available: { bg: '#ECFDF5', color: '#059669', label: 'Available' },
  claimed: { bg: '#EFF6FF', color: '#2563EB', label: 'Claimed' },
  expired: { bg: '#F3F4F6', color: '#6B7280', label: 'Expired' }
};

const InviteCodes = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, available: 0, claimed: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  // Generate form
  const [genPrefix, setGenPrefix] = useState('');
  const [genCount, setGenCount] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [toast, setToast] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await inviteCodesAPI.getStats();
      setStats(res.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inviteCodesAPI.getAll({
        page,
        limit: 20,
        status: statusFilter,
        search
      });
      setCodes(res.data.data.codes);
      setTotalPages(res.data.data.pagination.totalPages);
    } catch (err) {
      console.error('Failed to fetch codes:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!genPrefix.trim()) {
      setToast({ type: 'error', message: 'Enter a prefix' });
      return;
    }
    try {
      setGenerating(true);
      const res = await inviteCodesAPI.generate({
        prefix: genPrefix.trim(),
        count: genCount
      });
      setToast({ type: 'success', message: `Generated ${res.data.data.generated} codes` });
      setGenPrefix('');
      setGenCount(10);
      fetchCodes();
      fetchStats();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || 'Failed to generate' });
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invite code?')) return;
    try {
      await inviteCodesAPI.delete(id);
      setToast({ type: 'success', message: 'Code deleted' });
      fetchCodes();
      fetchStats();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || 'Failed to delete' });
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="ic-page">
          <h1 className="ic-title">Invite Codes</h1>

          {/* Stats */}
          <div className="ic-stats">
            <div className="ic-stat-card">
              <span className="ic-stat-value">{stats.total}</span>
              <span className="ic-stat-label">Total</span>
            </div>
            <div className="ic-stat-card ic-stat-available">
              <span className="ic-stat-value">{stats.available}</span>
              <span className="ic-stat-label">Available</span>
            </div>
            <div className="ic-stat-card ic-stat-claimed">
              <span className="ic-stat-value">{stats.claimed}</span>
              <span className="ic-stat-label">Claimed</span>
            </div>
            <div className="ic-stat-card ic-stat-expired">
              <span className="ic-stat-value">{stats.expired}</span>
              <span className="ic-stat-label">Expired</span>
            </div>
          </div>

          {/* Generate Form */}
          <div className="ic-generate-card">
            <h2 className="ic-generate-title">Generate Codes</h2>
            <form className="ic-generate-form" onSubmit={handleGenerate}>
              <div className="ic-form-group">
                <label>Prefix</label>
                <input
                  type="text"
                  value={genPrefix}
                  onChange={(e) => setGenPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                  placeholder="e.g. NTV"
                  maxLength={6}
                />
              </div>
              <div className="ic-form-group">
                <label>Count</label>
                <input
                  type="number"
                  value={genCount}
                  onChange={(e) => setGenCount(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                  min={1}
                  max={500}
                />
              </div>
              <button type="submit" className="ic-generate-btn" disabled={generating}>
                {generating ? 'Generating...' : 'Generate'}
              </button>
            </form>
            <p className="ic-generate-hint">Codes will look like: {genPrefix || 'NTV'}-A3F1</p>
          </div>

          {/* Filters */}
          <div className="ic-filters">
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="ic-filter-select"
            >
              <option value="">All Status</option>
              <option value="available">Available</option>
              <option value="claimed">Claimed</option>
              <option value="expired">Expired</option>
            </select>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search code or email..."
              className="ic-filter-search"
            />
          </div>

          {/* Table */}
          <div className="ic-table-wrap">
            <table className="ic-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>User</th>
                  <th>Application</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="ic-table-empty">Loading...</td></tr>
                ) : codes.length === 0 ? (
                  <tr><td colSpan={6} className="ic-table-empty">No codes found</td></tr>
                ) : codes.map((code) => {
                  const s = STATUS_COLORS[code.status] || STATUS_COLORS.available;
                  return (
                    <tr key={code.id}>
                      <td className="ic-code-cell">{code.code}</td>
                      <td>
                        <span className="ic-status-badge" style={{ background: s.bg, color: s.color }}>
                          {s.label}
                        </span>
                      </td>
                      <td>{code.user_email || '-'}</td>
                      <td>{code.app_id ? `#${code.app_id}` : '-'}</td>
                      <td className="ic-date-cell">{formatDate(code.created_at)}</td>
                      <td>
                        {code.status === 'available' && (
                          <button className="ic-delete-btn" onClick={() => handleDelete(code.id)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="ic-pagination">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
            </div>
          )}
        </div>

        {/* Toast */}
        {toast && (
          <div className={`ic-toast ic-toast-${toast.type}`} onClick={() => setToast(null)}>
            {toast.message}
          </div>
        )}
      </main>
    </div>
  );
};

export default InviteCodes;
